// src/features/tickets/services/TicketMailService.ts
//
// Schlanker Wrapper um den Netlify-Endpoint /.netlify/functions/send-ticket-reply.
// Verantwortlich fuer den Mail-Versand aus dem Ticketsystem heraus.
//
// Der Endpoint selbst (Schritt 4) kuemmert sich um:
//   - Laden der vorigen Message fuer Threading-Header (In-Reply-To, References)
//   - Subject-Token [XTAX #T-YYYY-NNNNN] (Threading-Fallback)
//   - Routing ueber send-email.js (Gmail-API-Backend)
//   - Persistierung von gmail_message_id / threadId zurueck in ticket_messages
//   - outbound_mail_log-Eintrag mit pipeline='tickets'
//
// Dieser Service ist absichtlich duenn — keine Mail-Komposition im Frontend.

import { TicketActor, TicketAttachmentInput, TicketSentAs } from '../types';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';

export interface TicketMailSendParams {
  ticket_id: string;
  ticket_number: string;
  subject: string;
  body_text: string;
  body_html?: string;
  to_emails: string[];
  cc_emails?: string[];
  attachments?: TicketAttachmentInput[];
  /** Eingebettete Inline-Bilder (per cid: im body_html referenziert). */
  inline_images?: TicketAttachmentInput[];
  actor: TicketActor;
  /**
   * Außenidentität „Senden als" (optional): bestimmt im Backend From-Anzeigename
   * und Signatur; actor_* bleibt die echte Person (Audit/Logs).
   */
  sent_as?: TicketSentAs;
  /** Erste Message eines neuen Tickets? Beeinflusst die Threading-Header-Wahl im Backend. */
  is_first_message: boolean;
  /** ID der bereits in ticket_messages persistierten Message, deren gmail_message_id/threadId nachgepflegt werden soll. */
  message_id: string;
}

export interface TicketMailSendResult {
  success: boolean;
  error?: string;
  gmail_message_id?: string;
  gmail_thread_id?: string;
}

class TicketMailService {
  private readonly endpoint = '/.netlify/functions/send-ticket-reply';

  /**
   * Schickt eine Ticket-Antwort per Mail.
   * Bei Erfolg pflegt der Backend-Endpoint die Gmail-IDs in ticket_messages
   * und schreibt einen outbound_mail_log-Eintrag.
   */
  async sendReply(params: TicketMailSendParams): Promise<TicketMailSendResult> {
    try {
      const response = await netlifyFetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_id: params.ticket_id,
          ticket_number: params.ticket_number,
          subject: params.subject,
          body_text: params.body_text,
          body_html: params.body_html,
          to_emails: params.to_emails,
          cc_emails: params.cc_emails,
          attachments: params.attachments,
          inline_images: params.inline_images,
          actor_staff_id: params.actor.staff_id,
          actor_email: params.actor.staff_email,
          actor_full_name: params.actor.staff_full_name,
          sent_as_staff_id: params.sent_as?.staff_id,
          sent_as_name: params.sent_as?.name,
          is_first_message: params.is_first_message,
          message_id: params.message_id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TicketMailService.sendReply] HTTP-Error:', response.status, errorText);
        return {
          success: false,
          error: `Mail-Versand fehlgeschlagen (HTTP ${response.status}): ${errorText.substring(0, 200)}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        gmail_message_id: data.gmail_message_id,
        gmail_thread_id: data.gmail_thread_id
      };
    } catch (error) {
      console.error('[TicketMailService.sendReply] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Mail-Versand'
      };
    }
  }
}

// Singleton-Export
export const ticketMailService = new TicketMailService();
